/**
 * Order-level tax exemption — the record on `order.taxExemption` and the tax the
 * exemption waived on `order.waivedTax`.
 *
 * SSOT: docs/TAX_EXEMPTION_ARCHITECTURE.md.
 *
 * TWO FIELDS, TWO OWNERS, ON PURPOSE.
 *
 *   - `taxExemption` is the AUTHORIZATION RECORD — who asked, which manager
 *     approved, the purchaser's details, the certificate photos, the history.
 *     It is SERVER-OWNED: only the API writes it (after the `manager_tax_exempt`
 *     gate), and `firestore.rules` refuses any client write that changes it. The
 *     POS reads it and never writes it back.
 *   - `waivedTax` is what the tax WOULD have been. It is computed by the POS tax
 *     engine — the one place tax is computed — and written with the rest of the
 *     order while the exemption is active (`null` otherwise).
 *
 * Every parser here is defensive: a malformed map (an old writer, a hand edit)
 * reads as ABSENT rather than throwing, because these are read inside order
 * streams, report builders and the warehouse sync, where one bad document must
 * never take down the whole read.
 */

import type { IAppliedTax } from './AppliedTax';
import { checkHoldsMoney, isCheckoutHeldOrderState } from './stateMachines';
import type { TaxType } from './types';

// ─── Limits (one place; the API, the phone page, the POS and the UI read these) ──

/** Longest exempt purchaser / organization name we keep. */
export const TAX_EXEMPTION_HOLDER_NAME_MAX = 120;
/** Longest exemption / certificate number we keep. */
export const TAX_EXEMPTION_NUMBER_MAX = 64;
/** History entries kept on the order (oldest dropped). The audit log keeps all. */
export const TAX_EXEMPTION_HISTORY_MAX = 50;
/** Certificate photos one exemption may carry, across every link and upload. */
export const TAX_EXEMPTION_DOCUMENT_MAX = 12;
/** Photos one phone upload link may add. */
export const TAX_EXEMPTION_UPLOAD_LINK_MAX_FILES = 6;
/** How long a phone upload link works. */
export const TAX_EXEMPTION_UPLOAD_LINK_TTL_MS = 2 * 60 * 60 * 1000;
/** Largest single photo accepted before it is re-encoded. */
export const TAX_EXEMPTION_DOCUMENT_MAX_BYTES = 20 * 1024 * 1024;
/** Signed read URLs for a certificate photo live this long. */
export const TAX_EXEMPTION_DOCUMENT_URL_TTL_SECONDS = 15 * 60;

// ─── Types ──────────────────────────────────────────────────────────────────

/** Why the purchaser is exempt. Optional — a fixed list, never free text. */
export const TAX_EXEMPTION_REASONS = [
  'government',
  'nonprofit',
  'religious',
  'educational',
  'diplomatic',
  'tribal',
  'other',
] as const;
export type TaxExemptionReason = (typeof TAX_EXEMPTION_REASONS)[number];

/** Human labels for {@link TaxExemptionReason} (management UI, reports, CSV). */
export const TAX_EXEMPTION_REASON_LABELS: Readonly<Record<TaxExemptionReason, string>> = {
  government: 'Government',
  nonprofit: 'Nonprofit / charitable',
  religious: 'Religious organization',
  educational: 'Educational institution',
  diplomatic: 'Diplomatic',
  tribal: 'Tribal government',
  other: 'Other',
};

export function isTaxExemptionReason(value: unknown): value is TaxExemptionReason {
  return typeof value === 'string' && (TAX_EXEMPTION_REASONS as readonly string[]).includes(value);
}

/**
 * A person on the record. The NAME is always resolved by the server from its
 * own records (the employee document, the user document) — never taken from a
 * request body.
 */
export type TaxExemptionActor =
  | {
      readonly kind: 'employee';
      readonly employeeId: number;
      readonly employeeGuid?: string;
      readonly name: string;
      /**
       * True when a server-signed operator assertion proved this employee was
       * the one at the till (a PIN approval, or their PIN session). False when
       * the till only NAMED them — accepted while `OPERATOR_ASSERTION_ENFORCE`
       * is off, and shown as such to reviewers.
       */
      readonly verified: boolean;
    }
  | { readonly kind: 'user'; readonly uid: string; readonly name: string }
  | { readonly kind: 'orderup-staff'; readonly uid: string; readonly name: string };

export type TaxExemptionDocumentSide = 'front' | 'back';
export const TAX_EXEMPTION_DOCUMENT_SIDES: readonly TaxExemptionDocumentSide[] = ['front', 'back'];

export type TaxExemptionDocumentSource = 'upload-link' | 'management';

export interface ITaxExemptionDocument {
  /** `book_attachments.id` — the bytes live in the private books-attachments bucket. */
  readonly id: string;
  readonly side: TaxExemptionDocumentSide;
  /** Always JPEG: every photo is re-encoded server-side (EXIF stripped). */
  readonly contentType: 'image/jpeg';
  readonly sizeBytes: number;
  readonly uploadedAt: Date;
  readonly source: TaxExemptionDocumentSource;
  /** Absent for a phone upload — the short-lived link is the authority. */
  readonly uploadedBy?: TaxExemptionActor;
}

export type TaxExemptionEventAction = 'applied' | 'updated' | 'removed' | 'document_added';
export type TaxExemptionEventSource = 'pos' | 'management' | 'upload-link';

export interface ITaxExemptionEvent {
  readonly action: TaxExemptionEventAction;
  readonly at: Date;
  readonly source: TaxExemptionEventSource;
  /** The employee signed in at the till who asked (the SERVER). */
  readonly requestedBy?: TaxExemptionActor;
  /** Whoever authorized it — the MANAGER whose PIN was entered, or a management user. */
  readonly approvedBy?: TaxExemptionActor;
  readonly holderName?: string;
  readonly exemptionNumber?: string;
  readonly reason?: TaxExemptionReason;
  readonly documentId?: string;
  readonly side?: TaxExemptionDocumentSide;
  /**
   * The till's Idempotency-Key for the action that wrote this entry (apply /
   * update / remove from the POS). A retry after a lost response is answered
   * from the entry carrying its key, before the approval gate.
   */
  readonly idempotencyKey?: string;
}

export interface ITaxExemption {
  /** True = tax is removed from the order. */
  readonly active: boolean;
  readonly holderName?: string;
  readonly exemptionNumber?: string;
  readonly reason?: TaxExemptionReason;
  /** The latest time it was switched ON. */
  readonly appliedAt: Date;
  /** The SERVER signed in at the till who asked for it. */
  readonly requestedBy?: TaxExemptionActor;
  /** The MANAGER whose PIN authorized it. */
  readonly approvedBy: TaxExemptionActor;
  readonly removedAt?: Date;
  readonly removedBy?: TaxExemptionActor;
  readonly removedRequestedBy?: TaxExemptionActor;
  /** Certificate photos, oldest first. The latest per side is the current certificate. */
  readonly documents: readonly ITaxExemptionDocument[];
  /** Append-only, oldest first, capped at {@link TAX_EXEMPTION_HISTORY_MAX}. */
  readonly history: readonly ITaxExemptionEvent[];
  readonly updatedAt: Date;
}

/** What the tax WOULD have been — computed by the POS while the order is exempt. */
export interface IWaivedTax {
  /** Dollars — the UNION taxable base (each item's discounted taxable amount counted once). */
  readonly taxableAmount: number;
  /** Dollars — Σ taxes. */
  readonly taxAmount: number;
  /** Per rate, the same shape as `appliedTaxes`. */
  readonly taxes: readonly IAppliedTax[];
}

// ─── Parsing ────────────────────────────────────────────────────────────────

function toDate(value: unknown): Date | undefined {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  if (value && typeof value === 'object') {
    const v = value as { toDate?: unknown; seconds?: unknown; _seconds?: unknown };
    if (typeof v.toDate === 'function') {
      const d = (v.toDate as () => Date)();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : undefined;
    }
    const seconds = typeof v.seconds === 'number' ? v.seconds : typeof v._seconds === 'number' ? v._seconds : undefined;
    if (seconds !== undefined) return new Date(seconds * 1000);
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

function str(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
}

export function parseTaxExemptionActor(raw: unknown): TaxExemptionActor | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const name = str(r.name, 200);
  if (!name) return undefined;
  if (r.kind === 'employee') {
    const employeeId = typeof r.employeeId === 'number' && Number.isFinite(r.employeeId) ? r.employeeId : undefined;
    if (employeeId === undefined) return undefined;
    const employeeGuid = str(r.employeeGuid, 128);
    return {
      kind: 'employee',
      employeeId,
      ...(employeeGuid ? { employeeGuid } : {}),
      name,
      verified: r.verified === true,
    };
  }
  if (r.kind === 'user' || r.kind === 'orderup-staff') {
    const uid = str(r.uid, 128);
    if (!uid) return undefined;
    return { kind: r.kind, uid, name };
  }
  return undefined;
}

function parseDocument(raw: unknown): ITaxExemptionDocument | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const id = str(r.id, 128);
  const uploadedAt = toDate(r.uploadedAt);
  if (!id || !uploadedAt) return undefined;
  if (r.side !== 'front' && r.side !== 'back') return undefined;
  const source: TaxExemptionDocumentSource = r.source === 'management' ? 'management' : 'upload-link';
  const uploadedBy = parseTaxExemptionActor(r.uploadedBy);
  return {
    id,
    side: r.side,
    contentType: 'image/jpeg',
    sizeBytes: typeof r.sizeBytes === 'number' && r.sizeBytes > 0 ? r.sizeBytes : 0,
    uploadedAt,
    source,
    ...(uploadedBy ? { uploadedBy } : {}),
  };
}

const EVENT_ACTIONS: readonly TaxExemptionEventAction[] = ['applied', 'updated', 'removed', 'document_added'];
const EVENT_SOURCES: readonly TaxExemptionEventSource[] = ['pos', 'management', 'upload-link'];

function parseEvent(raw: unknown): ITaxExemptionEvent | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const at = toDate(r.at);
  if (!at) return undefined;
  if (!EVENT_ACTIONS.includes(r.action as TaxExemptionEventAction)) return undefined;
  const source = EVENT_SOURCES.includes(r.source as TaxExemptionEventSource)
    ? (r.source as TaxExemptionEventSource)
    : 'pos';
  const requestedBy = parseTaxExemptionActor(r.requestedBy);
  const approvedBy = parseTaxExemptionActor(r.approvedBy);
  const holderName = str(r.holderName, TAX_EXEMPTION_HOLDER_NAME_MAX);
  const exemptionNumber = str(r.exemptionNumber, TAX_EXEMPTION_NUMBER_MAX);
  const documentId = str(r.documentId, 128);
  // Kept only when it is EXACTLY a key the API accepts (16-128 of
  // [A-Za-z0-9_-]) — never trimmed or cut, or a lookup by the raw header
  // could match a key nobody sent.
  const idempotencyKey =
    typeof r.idempotencyKey === 'string' && /^[A-Za-z0-9_-]{16,128}$/.test(r.idempotencyKey) ? r.idempotencyKey : undefined;
  return {
    action: r.action as TaxExemptionEventAction,
    at,
    source,
    ...(requestedBy ? { requestedBy } : {}),
    ...(approvedBy ? { approvedBy } : {}),
    ...(holderName ? { holderName } : {}),
    ...(exemptionNumber ? { exemptionNumber } : {}),
    ...(isTaxExemptionReason(r.reason) ? { reason: r.reason } : {}),
    ...(documentId ? { documentId } : {}),
    ...(r.side === 'front' || r.side === 'back' ? { side: r.side } : {}),
    ...(idempotencyKey ? { idempotencyKey } : {}),
  };
}

/**
 * The exemption a stored map describes, or `undefined` when there is none or it
 * is unreadable. `approvedBy` and `appliedAt` are required: an exemption nobody
 * approved is not an exemption.
 */
export function parseTaxExemption(raw: unknown): ITaxExemption | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const appliedAt = toDate(r.appliedAt);
  const approvedBy = parseTaxExemptionActor(r.approvedBy);
  if (!appliedAt || !approvedBy) return undefined;
  const holderName = str(r.holderName, TAX_EXEMPTION_HOLDER_NAME_MAX);
  const exemptionNumber = str(r.exemptionNumber, TAX_EXEMPTION_NUMBER_MAX);
  const requestedBy = parseTaxExemptionActor(r.requestedBy);
  const removedAt = toDate(r.removedAt);
  const removedBy = parseTaxExemptionActor(r.removedBy);
  const removedRequestedBy = parseTaxExemptionActor(r.removedRequestedBy);
  const documents = Array.isArray(r.documents)
    ? r.documents.map(parseDocument).filter((d): d is ITaxExemptionDocument => d !== undefined)
    : [];
  const history = Array.isArray(r.history)
    ? r.history.map(parseEvent).filter((e): e is ITaxExemptionEvent => e !== undefined)
    : [];
  return {
    active: r.active === true,
    ...(holderName ? { holderName } : {}),
    ...(exemptionNumber ? { exemptionNumber } : {}),
    ...(isTaxExemptionReason(r.reason) ? { reason: r.reason } : {}),
    appliedAt,
    ...(requestedBy ? { requestedBy } : {}),
    approvedBy,
    ...(removedAt ? { removedAt } : {}),
    ...(removedBy ? { removedBy } : {}),
    ...(removedRequestedBy ? { removedRequestedBy } : {}),
    documents,
    history,
    updatedAt: toDate(r.updatedAt) ?? appliedAt,
  };
}

function parseAppliedTaxLike(raw: unknown): IAppliedTax | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.name !== 'string') return undefined;
  const type: TaxType = r.type === 'percentage' || r.type === 'fixed' ? r.type : r.type === 'percent' ? 'percentage' : 'none';
  return {
    taxRateId: typeof r.taxRateId === 'number' ? r.taxRateId : 0,
    name: r.name,
    rate: typeof r.rate === 'number' ? r.rate : 0,
    type,
    taxableBaseAmount: typeof r.taxableBaseAmount === 'number' ? r.taxableBaseAmount : 0,
    amountCalculated: typeof r.amountCalculated === 'number' ? r.amountCalculated : 0,
  };
}

/** The waived tax a stored map describes, or `undefined`. */
export function parseWaivedTax(raw: unknown): IWaivedTax | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const taxes = Array.isArray(r.taxes)
    ? r.taxes.map(parseAppliedTaxLike).filter((t): t is IAppliedTax => t !== undefined)
    : [];
  const taxAmount =
    typeof r.taxAmount === 'number' && Number.isFinite(r.taxAmount)
      ? r.taxAmount
      : taxes.reduce((sum, t) => sum + t.amountCalculated, 0);
  const taxableAmount = typeof r.taxableAmount === 'number' && Number.isFinite(r.taxableAmount) ? r.taxableAmount : 0;
  return { taxableAmount: Math.max(0, taxableAmount), taxAmount: Math.max(0, taxAmount), taxes };
}

// ─── Reading ────────────────────────────────────────────────────────────────

/** True when tax is currently removed from the order. */
export function isOrderTaxExempt(order: { readonly taxExemption?: unknown }): boolean {
  const exemption = order.taxExemption;
  if (!exemption || typeof exemption !== 'object') return false;
  return (exemption as { active?: unknown }).active === true;
}

/**
 * The order as a CUSTOMER may receive it: without `order.taxExemption`.
 *
 * The record is staff audit data — who asked for the exemption, which manager
 * approved it (employee names and ids, a management user's uid), its history
 * and the certificate document ids. A customer holding a receipt link has no
 * business with any of it, and a receipt link can be forwarded. What the
 * customer is owed is already on the receipt ("Tax exempt #N") and in the
 * totals. `waivedTax` stays: it is arithmetic on the customer's own bill.
 *
 * Staff read the record through the review route, which checks access and
 * audits certificate views; this projection is for every other caller.
 */
export function withoutStaffOnlyOrderFields<T extends object>(orderJson: T): Omit<T, 'taxExemption'> {
  // `T extends object`, not `{ taxExemption?: unknown }`: a constraint whose every member is
  // optional is a WEAK TYPE, and an order that was never exempt has no key in common with it,
  // so TypeScript refuses the call (TS2559) at exactly the call sites this must serve.
  const { taxExemption: _staffOnly, ...customerFacing } = orderJson as T & { taxExemption?: unknown };
  return customerFacing as Omit<T, 'taxExemption'>;
}

/** The latest photo of each side — the certificate as it stands now. */
export function currentTaxExemptionDocuments(
  exemption: Pick<ITaxExemption, 'documents'> | undefined,
): { front?: ITaxExemptionDocument; back?: ITaxExemptionDocument } {
  const out: { front?: ITaxExemptionDocument; back?: ITaxExemptionDocument } = {};
  for (const doc of exemption?.documents ?? []) {
    const current = out[doc.side];
    if (!current || current.uploadedAt.getTime() <= doc.uploadedAt.getTime()) out[doc.side] = doc;
  }
  return out;
}

/** How the certificate stands, for a list column. */
export type TaxExemptionCertificateStatus = 'complete' | 'front-only' | 'back-only' | 'missing';

export function taxExemptionCertificateStatus(
  exemption: Pick<ITaxExemption, 'documents'> | undefined,
): TaxExemptionCertificateStatus {
  const { front, back } = currentTaxExemptionDocuments(exemption);
  if (front && back) return 'complete';
  if (front) return 'front-only';
  if (back) return 'back-only';
  return 'missing';
}

/** Display name of an actor, for a line on screen or in a CSV. */
export function taxExemptionActorName(actor: TaxExemptionActor | undefined): string {
  return actor?.name ?? '';
}

// ─── The money rule ─────────────────────────────────────────────────────────

/** Order states in which the tax on an order can no longer change. */
const TERMINAL_ORDER_STATES = new Set(['closed', 'cancelled', 'voided', 'transferred']);

/**
 * Check payment statuses that, BEYOND {@link checkHoldsMoney}, still forbid a
 * tax change: a capture that failed on replay (`paidFailed` — the intent that
 * will be retried was sized to the old total) and a refund (`refunded` — money
 * moved, and re-taxing it would reopen a settled bill).
 */
const TAX_CHANGE_BLOCKING_STATUSES = new Set(['paidFailed', 'refunded']);

/** Order payment statuses that mean the same at the order level. */
const MONEY_ORDER_PAYMENT_STATUSES = new Set([
  'processing',
  'partiallyPaid',
  'paid',
  'partiallyRefunded',
  'refunded',
]);

/** A check that no longer takes part in the bill. */
const INERT_CHECK_STATES = new Set(['voided', 'superseded']);

export type TaxExemptionChangeRefusal = 'order-not-open' | 'marketplace' | 'payment-exists';

interface MoneyCheckFields {
  readonly checkState?: string | null;
  readonly paymentStatus?: string | null;
  readonly paidAmount?: number | null;
  readonly tenders?: readonly unknown[] | null;
}

/**
 * True when this check stops the order's tax from changing: it holds money
 * ({@link checkHoldsMoney} — the one definition every payment-safety gate
 * shares), a payment was attempted or refunded on it, or it records a tender.
 * A voided or superseded check no longer takes part in the bill and never
 * blocks.
 */
export function checkBlocksTaxExemptionChange(check: MoneyCheckFields): boolean {
  if (check.checkState && INERT_CHECK_STATES.has(check.checkState)) return false;
  if (
    checkHoldsMoney({
      ...(typeof check.paidAmount === 'number' ? { paidAmount: check.paidAmount } : {}),
      ...(typeof check.paymentStatus === 'string' ? { paymentStatus: check.paymentStatus } : {}),
    })
  ) {
    return true;
  }
  if (check.paymentStatus && TAX_CHANGE_BLOCKING_STATUSES.has(check.paymentStatus)) return true;
  return Array.isArray(check.tenders) && check.tenders.length > 0;
}

/**
 * Why the exemption may NOT be switched on or off right now, or `null` when it
 * may. One rule, read by the API (authoritative) and by every UI that offers
 * the action — so the till and the server can never disagree about when.
 *
 * Money already collected is reversed through a void or refund first: changing
 * the tax under a payment would leave a check owing, or overpaid, with nothing
 * to reconcile it against. That is the same rule as every other change to what
 * a guest owes.
 */
export function taxExemptionChangeRefusal(
  order: {
    readonly orderState?: string | null;
    readonly paymentStatus?: string | null;
    readonly paidAmount?: number | null;
    readonly orderSource?: string | null;
    readonly externalSource?: string | null;
  },
  checks: readonly MoneyCheckFields[],
  isMarketplace: (order: { readonly orderSource?: string | null; readonly externalSource?: string | null }) => boolean,
): TaxExemptionChangeRefusal | null {
  if (order.orderState && TERMINAL_ORDER_STATES.has(order.orderState)) return 'order-not-open';
  // Checkout-held (unpaid online order, not yet an order anyone may change).
  if (isCheckoutHeldOrderState(order.orderState)) return 'order-not-open';
  if (isMarketplace(order)) return 'marketplace';
  // Same threshold as `checkHoldsMoney` (any positive amount): one definition of "money".
  if ((order.paidAmount ?? 0) > 0) return 'payment-exists';
  if (order.paymentStatus && MONEY_ORDER_PAYMENT_STATUSES.has(order.paymentStatus)) return 'payment-exists';
  if (checks.some(checkBlocksTaxExemptionChange)) return 'payment-exists';
  return null;
}
